<template>
  <a-modal
    :title="$t('forgotPwd.title')"
    :visible="visible"
    :confirm-loading="resetLoading"
    :ok-text="$t('forgotPwd.submit')"
    :cancel-text="$t('forgotPwd.cancel')"
    :mask-closable="false"
    :width="460"
    @ok="handleReset"
    @cancel="handleClose"
  >
    <a-form :form="form" layout="vertical">
      <a-form-item :label="$t('login.username')">
        <a-input
          size="large"
          :placeholder="$t('login.inputUsername')"
          v-decorator="['username', {rules: [{ required: true, message: $t('login.inputUsername'), whitespace: true }]}]"
        >
          <a-icon slot="prefix" type="user"></a-icon>
        </a-input>
      </a-form-item>

      <a-form-item :label="$t('forgotPwd.email')">
        <a-input
          size="large"
          :placeholder="$t('forgotPwd.inputEmail')"
          v-decorator="['email', {rules: [{ required: true, message: $t('forgotPwd.inputEmail'), whitespace: true }, { type: 'email', message: $t('forgotPwd.invalidEmail') }]}]"
        >
          <a-icon slot="prefix" type="mail"></a-icon>
        </a-input>
      </a-form-item>

      <a-form-item :label="$t('forgotPwd.code')">
        <div class="code-row">
          <a-input
            size="large"
            class="code-input"
            :placeholder="$t('forgotPwd.inputCode')"
            v-decorator="['code', {rules: [{ required: true, message: $t('forgotPwd.inputCode'), whitespace: true }]}]"
          >
            <a-icon slot="prefix" type="safety"></a-icon>
          </a-input>
          <a-button
            size="large"
            class="code-btn"
            :disabled="countdown > 0"
            :loading="sendLoading"
            @click="handleSendCode"
          >
            {{ countdown > 0 ? countdown + 's' : $t('forgotPwd.sendCode') }}
          </a-button>
        </div>
      </a-form-item>

      <a-form-item :label="$t('forgotPwd.newPassword')">
        <a-input
          size="large"
          type="password"
          autocomplete="new-password"
          :placeholder="$t('forgotPwd.inputNewPassword')"
          v-decorator="['newPassword', {rules: [{ required: true, message: $t('forgotPwd.inputNewPassword') }, { validator: validateNewPassword }]}]"
        >
          <a-icon slot="prefix" type="lock"></a-icon>
        </a-input>
      </a-form-item>

      <a-form-item :label="$t('forgotPwd.confirmPassword')">
        <a-input
          size="large"
          type="password"
          autocomplete="new-password"
          :placeholder="$t('forgotPwd.inputConfirmPassword')"
          v-decorator="['confirmPassword', {rules: [{ required: true, message: $t('forgotPwd.inputConfirmPassword') }, { validator: validateConfirmPassword }]}]"
        >
          <a-icon slot="prefix" type="lock"></a-icon>
        </a-input>
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script>
export default {
  name: 'ForgotPassword',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    // 打开时可用于预填的用户名（来自登录表单）
    username: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      sendLoading: false,
      resetLoading: false,
      countdown: 0,
      timer: null
    }
  },
  beforeCreate () {
    this.form = this.$form.createForm(this)
  },
  watch: {
    visible (val) {
      if (val) {
        // 打开时重置倒计时并预填用户名
        this.clearTimer()
        this.countdown = 0
        if (this.username) {
          this.$nextTick(() => {
            this.form.setFieldsValue({ username: this.username })
          })
        }
      }
    }
  },
  beforeDestroy () {
    this.clearTimer()
  },
  methods: {
    validateNewPassword (rule, value, callback) {
      if (value && value.length < 6) {
        callback(new Error(this.$t('forgotPwd.passwordTooShort')))
      } else {
        callback()
      }
    },
    validateConfirmPassword (rule, value, callback) {
      const pwd = this.form.getFieldValue('newPassword')
      if (value && pwd && value !== pwd) {
        callback(new Error(this.$t('forgotPwd.passwordNotMatch')))
      } else {
        callback()
      }
    },
    handleSendCode () {
      this.form.validateFields(['username', 'email'], (errors, values) => {
        if (errors) {
          return
        }
        this.sendLoading = true
        this.$post('forgot-password/send-code', {
          username: values.username,
          email: values.email
        }).then((r) => {
          this.sendLoading = false
          if (r.data && r.data.code === 200) {
            this.$message.success(r.data.message || this.$t('forgotPwd.sendSuccess'))
            this.startCountdown()
          } else {
            this.$message.error((r.data && r.data.message) || this.$t('forgotPwd.sendFail'))
          }
        }).catch(() => {
          // 网络或限流异常，全局响应拦截器已提示
          this.sendLoading = false
        })
      })
    },
    handleReset () {
      this.form.validateFields((errors, values) => {
        if (errors) {
          return
        }
        this.resetLoading = true
        this.$post('forgot-password/reset', {
          username: values.username,
          email: values.email,
          code: values.code,
          password: this.$aesEncrypt.encrypt(values.newPassword)
        }).then((r) => {
          this.resetLoading = false
          if (r.data && r.data.code === 200) {
            this.$message.success(r.data.message || this.$t('forgotPwd.resetSuccess'))
            this.$emit('success')
            this.handleClose()
          } else {
            this.$message.error((r.data && r.data.message) || this.$t('forgotPwd.resetFail'))
          }
        }).catch(() => {
          // 网络或限流异常，全局响应拦截器已提示
          this.resetLoading = false
        })
      })
    },
    startCountdown () {
      this.countdown = 60
      this.clearTimer()
      this.timer = setInterval(() => {
        if (this.countdown <= 1) {
          this.countdown = 0
          this.clearTimer()
        } else {
          this.countdown--
        }
      }, 1000)
    },
    clearTimer () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    handleClose () {
      this.clearTimer()
      this.countdown = 0
      this.sendLoading = false
      this.resetLoading = false
      this.form.resetFields()
      this.$emit('close')
    }
  }
}
</script>

<style lang="less" scoped>
  .code-row {
    display: flex;
    align-items: center;
    .code-input {
      flex: 1;
    }
    .code-btn {
      width: 130px;
      margin-left: 10px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/views/login/ForgotPassword.vue